import apiSlice from '../apiSlice';
import { GlobalFilterKeys } from '../types';
import { stripGlobalFilterKeys } from '../utils';

import {
  RawReport,
  Report,
  ReportDownloadUrl,
  ReportingSearchParams,
  ReportRequestData,
} from '@/features/reporting';
import { normalizeReport } from '@/features/reporting/utils/normalizeReport';
import { PaginationResponse } from '@/shared/utils/types';

const REPORTS_API_URL = '/api/reports';

const reportsTags = apiSlice.enhanceEndpoints({
  addTagTypes: ['reports'],
});

const reportsApiSlice = reportsTags.injectEndpoints({
  endpoints: (builder) => ({
    getReports: builder.query<
      PaginationResponse<Report>,
      Pick<GlobalFilterKeys, 'platformId'> & ReportingSearchParams
    >({
      query: (params) => ({
        url: `${REPORTS_API_URL}/`,
        method: 'GET',
        params: stripGlobalFilterKeys(params),
      }),
      transformResponse: (response: PaginationResponse<RawReport>) => {
        const { results, ...pagination } = response;

        return {
          ...pagination,
          results: results.map(normalizeReport),
        };
      },
      providesTags: [{ type: 'reports', id: 'LIST' }],
    }),

    getReport: builder.query<Report, number>({
      query: (id) => ({
        url: `${REPORTS_API_URL}/${String(id)}/`,
        method: 'GET',
      }),
      transformResponse: (response: RawReport) => normalizeReport(response),
      providesTags: (_result, _error, id) => [
        { type: 'reports', id: String(id) },
      ],
    }),

    // Returns a pre-signed S3 URL for the already-generated report file.
    getReportDownloadUrl: builder.query<ReportDownloadUrl, number>({
      query: (id) => ({
        url: `${REPORTS_API_URL}/${String(id)}/download-url`,
        method: 'GET',
      }),
      // Pre-signed URLs expire, so never hand back a cached one.
      keepUnusedDataFor: 0,
    }),

    // Async: returns a `processing` report. We don't invalidate the list here —
    // the WebSocket does that on completion, so the row only shows once ready.
    generateReport: builder.mutation<Report, ReportRequestData>({
      query: (data) => ({
        url: `${REPORTS_API_URL}/`,
        method: 'POST',
        data,
      }),
      transformResponse: (response: RawReport) => normalizeReport(response),
    }),
  }),
});

export const {
  useGenerateReportMutation,
  useGetReportsQuery,
  useGetReportQuery,
  useLazyGetReportDownloadUrlQuery,
} = reportsApiSlice;

export { reportsApiSlice };
